/**
 * MissionAssist App Launcher (P4-PHASE1-NAV-REORG).
 *
 * A floating application-chooser panel in the global top header, sitting
 * immediately before the Notification bell. Interaction model is the familiar
 * Google/Gmail nine-dot launcher: a grid icon opens a panel of applications,
 * each with an icon, a name and a generous click target.
 *
 * Authorization: NONE happens here. This component renders exactly the
 * `items` it is handed. Those items were already filtered by
 * `getNavigation()` in lib/navigationConfig.js using the same
 * `effectiveModules` array the route guards use. If a destination is not
 * authorized it never reaches this component.
 *
 * Imports are relative rather than the "@/" alias: that alias is
 * webpack-only in this repo (craco.config.js `webpack.resolve.alias`) with no
 * corresponding Jest `moduleNameMapper`, so "@/" does not resolve under
 * `yarn test`. Same rationale, and same choice, as features/emails/EmailsShell.jsx.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LayoutGrid } from "lucide-react";

import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

export const APP_LAUNCHER_LABEL = "MissionAssist Apps";

/**
 * @param {Array}    items      launcher destinations, already permission-filtered
 * @param {Function} onNavigate optional guard hook — receives (event, to); call
 *                              preventDefault() inside to block navigation
 *                              (used for the in-run exit guard, unchanged)
 */
export default function AppLauncher({ items = [], onNavigate }) {
  const [open, setOpen] = useState(false);

  // Nothing this user is entitled to reach -> no control at all. Never render
  // an empty launcher, and never render a launcher that implies access.
  if (!items.length) return null;

  const handleItemClick = (event, to) => {
    if (typeof onNavigate === "function") {
      onNavigate(event, to);
    }
    if (!event.defaultPrevented) {
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="secondary"
                aria-label={APP_LAUNCHER_LABEL}
                className="h-10 w-10 rounded-full bg-white/10 p-0 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                data-testid="app-launcher-trigger"
              >
                <LayoutGrid className="h-5 w-5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Apps</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-[min(92vw,26rem)] border-white/10 bg-slate-950 p-0 text-slate-100 ring-1 ring-white/10"
        data-testid="app-launcher-panel"
      >
        <div className="border-b border-white/10 px-5 py-3">
          <div className="text-sm font-semibold text-slate-100">MissionAssist</div>
          <div className="text-xs text-slate-200/60">Applications</div>
        </div>

        <div
          className="grid max-h-[60vh] grid-cols-3 gap-1 overflow-y-auto p-3"
          data-testid="app-launcher-grid"
        >
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              onClick={(event) => handleItemClick(event, it.to)}
              aria-current={it.active ? "page" : undefined}
              className={
                "group flex min-h-[104px] flex-col items-center justify-start gap-2 rounded-xl px-2 py-4 text-center transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 " +
                (it.active ? "bg-indigo-500/15" : "hover:bg-white/5")
              }
              data-testid={`app-launcher-item-${it.id}`}
            >
              <span
                className={
                  "flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition " +
                  (it.active ? "bg-indigo-500/25" : "bg-white/5 group-hover:bg-indigo-500/10")
                }
              >
                <it.icon className="h-[22px] w-[22px] text-slate-100" />
              </span>
              <span className="text-xs font-medium leading-tight text-slate-100">
                {it.label}
              </span>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
